import React, { useState } from 'react';
import { Wordmark } from '../components';
import { Camera, Clock, Users, MapPin, Calendar, Star, Check, ArrowRight, Mail, Phone, Building, Mic, PartyPopper, Trophy, Rocket, Heart, ChevronLeft, ChevronRight } from 'lucide-react';

export function Photography() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    venue: '',
    attendees: '',
    package: 'standard',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const packages = [
    {
      id: 'basic',
      name: 'Basic Package',
      duration: '2 hours',
      price: '$275',
      features: [
        'Professional photographer',
        '2 hours coverage',
        '75 edited photos',
        '5-day delivery',
        'Online gallery'
      ],
      popular: false
    },
    {
      id: 'standard',
      name: 'Standard Package',
      duration: '4 hours',
      price: '$899',
      features: [
        'Professional photographer',
        '4 hours coverage',
        '150 edited photos',
        '3-day delivery',
        'Online gallery',
        '10 premium prints',
        'One – 60-second social media reel'
      ],
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Package',
      duration: 'Full day',
      price: '$1,499',
      features: [
        'Professional photographer + assistant',
        'Full day coverage (8 hours)',
        '400+ edited photos',
        '2-day delivery',
        'Online gallery',
        '20 premium prints',
        'Drone shots (if venue permits)',
        'Same-day preview photos'
      ],
      popular: false
    },
    {
      id: 'custom',
      name: 'Custom Package',
      duration: 'Flexible',
      price: 'Custom Quote',
      features: [
        'Tailored coverage duration',
        'Custom photo delivery quantity',
        'Flexible delivery timeline',
        'Specialized editing style',
        'Additional services available',
        'Personalized consultation'
      ],
      popular: false
    }
  ];

  const photographyTypes = [
    { category: 'Corporate Events', icon: Building },
    { category: 'Conferences', icon: Mic },
    { category: 'Private Parties', icon: PartyPopper },
    { category: 'Sports Events', icon: Trophy },
    { category: 'Product Launches', icon: Rocket },
    { category: 'Weddings', icon: Heart }
  ];

  const testimonials = [
    {
      name: 'K. Brown',
      role: 'Event Organizer',
      content: 'Absolutely stunning photos! Our party looked incredible and Sean captured every important moment.',
      rating: 5
    },
    {
      name: 'Josiah Blair',
      role: 'Youth Soccer Player',
      content: 'Professional and delivered amazing photos for my sports reel. Highly recommend!',
      rating: 5
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Submit to the API endpoint
    fetch('/api/submit-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        formName: 'photography-booking', 
        ...formData,
        package: packages.find(p => p.id === formData.package)?.name || formData.package
      }),
    }).then(() => {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    }).catch(() => {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const portfolioImages = [
    { id: 1, title: 'Marathon Finish Line', category: 'Sports' },
    { id: 2, title: 'Basketball Championship', category: 'Sports' },
    { id: 3, title: 'Soccer Tournament', category: 'Sports' },
    { id: 4, title: 'Track & Field', category: 'Sports' },
    { id: 5, title: 'Swimming Competition', category: 'Sports' },
    { id: 6, title: 'Cycling Race', category: 'Sports' },
    { id: 7, title: 'Tennis Match', category: 'Sports' },
    { id: 8, title: 'Golf Tournament', category: 'Sports' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % portfolioImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + portfolioImages.length) % portfolioImages.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Navigation */}
      <nav style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        padding: '16px 24px',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--line)'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <Wordmark size={28} />
          </a>
          <a 
            href="/" 
            style={{ 
              fontSize: 14, 
              color: 'var(--ink-2)', 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            ← Back to VentyVIP
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{ 
        padding: '140px 24px 80px', 
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff'
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 20px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 24
          }}>
            <Camera size={20} />
            Professional Event Photography
          </div>
          
          <h1 style={{ 
            fontSize: 'clamp(40px, 6vw, 64px)', 
            fontWeight: 700, 
            lineHeight: 1.1, 
            marginBottom: 24
          }}>
            Capture Your Event<br />
            <span style={{ color: '#ffd700' }}>Perfectly.</span>
          </h1>
          
          <p style={{ 
            fontSize: 'clamp(18px, 2.5vw, 22px)', 
            opacity: 0.9, 
            maxWidth: 600, 
            margin: '0 auto 40px',
            lineHeight: 1.6
          }}>
            Professional photography for conferences, corporate events, and celebrations. 
            Stunning images delivered fast.
          </p>

          <div style={{ 
            display: 'flex', 
            gap: 20, 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            fontSize: 14
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Check size={16} style={{ color: '#ffd700' }} />
              500+ Events Captured
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Check size={16} style={{ color: '#ffd700' }} />
              48-Hour Delivery
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Check size={16} style={{ color: '#ffd700' }} />
              Satisfaction Guaranteed
            </div>
          </div>
        </div>
      </section>

      {/* Photography Types */}
      <section style={{ padding: '40px 24px', background: 'var(--bg-2)' }}>
        <div style={{ 
          maxWidth: 1000, 
          margin: '0 auto', 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
          gap: 24,
          textAlign: 'center'
        }}>
          {photographyTypes.map((type, i) => (
            <div key={i}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: 8,
                color: 'var(--primary)'
              }}>
                <type.icon size={28} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                {type.category}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Portfolio Carousel */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 36, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 16,
            color: 'var(--ink)'
          }}>
            Our Work
          </h2>
          <p style={{ 
            fontSize: 18, 
            color: 'var(--ink-2)', 
            textAlign: 'center', 
            maxWidth: 600, 
            margin: '0 auto 48px'
          }}>
            Specializing in sports event photography and dynamic action shots
          </p>

          {/* Main Carousel */}
          <div style={{ 
            position: 'relative',
            background: 'var(--bg-2)',
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid var(--line)',
            marginBottom: 32
          }}>
            {/* Current Image */}
            <div style={{
              width: '100%',
              aspectRatio: '16/9',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 24,
              fontWeight: 600,
              position: 'relative'
            }}>
              {/* Replace this div with your actual image */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📸</div>
                <div>{portfolioImages[currentImageIndex].title}</div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                style={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
              >
                <ChevronLeft size={24} />
              </button>

              <button
                onClick={nextImage}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Image Info */}
            <div style={{ padding: 24 }}>
              <h3 style={{ 
                fontSize: 20, 
                fontWeight: 600, 
                color: 'var(--ink)', 
                marginBottom: 8 
              }}>
                {portfolioImages[currentImageIndex].title}
              </h3>
              <p style={{ 
                fontSize: 14, 
                color: 'var(--ink-3)', 
                marginBottom: 16 
              }}>
                {portfolioImages[currentImageIndex].category} Photography
              </p>
              <p style={{ 
                fontSize: 16, 
                color: 'var(--ink-2)', 
                lineHeight: 1.6 
              }}>
                Professional sports photography capturing the intensity and emotion of athletic competition. 
                Perfect for athletes, teams, and sports organizations.
              </p>
            </div>
          </div>

          {/* Thumbnail Navigation */}
          <div style={{ 
            display: 'flex', 
            gap: 12, 
            justifyContent: 'center',
            marginBottom: 32,
            flexWrap: 'wrap'
          }}>
            {portfolioImages.map((image, index) => (
              <button
                key={image.id}
                onClick={() => goToImage(index)}
                style={{
                  width: 80,
                  height: 60,
                  borderRadius: 8,
                  border: currentImageIndex === index 
                    ? '3px solid var(--primary)' 
                    : '2px solid var(--line)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  if (currentImageIndex !== index) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                📸
              </button>
            ))}
          </div>

          {/* Navigation Dots */}
          <div style={{ 
            display: 'flex', 
            gap: 8, 
            justifyContent: 'center',
            marginBottom: 32
          }}>
            {portfolioImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                style={{
                  width: currentImageIndex === index ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  border: 'none',
                  background: currentImageIndex === index 
                    ? 'var(--primary)' 
                    : 'var(--line)',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <a 
              href="mailto:media@ventyvip.com?subject=VentyVIP%20Photography%20Portfolio%20Request&body=Hi%20there!%0A%0AI'm%20interested%20in%20seeing%20your%20full%20photography%20portfolio.%0A%0APlease%20share%20more%20of%20your%20work%20with%20me.%0A%0AThanks!"
              style={{
                padding: '14px 28px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                textDecoration: 'none',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Request Full Portfolio
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 40, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 16,
            color: 'var(--ink)'
          }}>
            Choose Your Package
          </h2>
          <p style={{ 
            fontSize: 18, 
            color: 'var(--ink-2)', 
            textAlign: 'center', 
            maxWidth: 600, 
            margin: '0 auto 60px'
          }}>
            Flexible packages designed for events of all sizes
          </p>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 32 
          }}>
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                style={{
                  padding: 32,
                  background: pkg.popular 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'var(--bg)',
                  borderRadius: 16,
                  border: pkg.popular 
                    ? '2px solid #667eea' 
                    : '1px solid var(--line)',
                  position: 'relative',
                  color: pkg.popular ? '#fff' : 'var(--ink)'
                }}
              >
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '6px 16px',
                    background: '#ffd700',
                    color: '#1a1a1a',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}>
                    Most Popular
                  </div>
                )}
                
                <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
                  {pkg.name}
                </h3>
                <div style={{ 
                  fontSize: 14, 
                  opacity: 0.8, 
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <Clock size={16} />
                  {pkg.duration}
                </div>
                <div style={{ 
                  fontSize: 36, 
                  fontWeight: 700, 
                  marginBottom: 24,
                  color: pkg.popular ? '#fff' : 'var(--primary)'
                }}>
                  {pkg.price}
                </div>
                
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0, 
                  display: 'grid', 
                  gap: 12 
                }}>
                  {pkg.features.map((feature, i) => (
                    <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <Check size={16} style={{ 
                        color: pkg.popular ? '#ffd700' : 'var(--primary)',
                        flexShrink: 0,
                        marginTop: 2
                      }} />
                      <span style={{ fontSize: 14 }}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 24px', background: 'var(--bg-2)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 36, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 48,
            color: 'var(--ink)'
          }}>
            What Our Clients Say
          </h2>
          
          <div style={{ display: 'grid', gap: 32 }}>
            {testimonials.map((testimonial, i) => (
              <div 
                key={i}
                style={{
                  padding: 32,
                  background: 'var(--bg)',
                  borderRadius: 16,
                  border: '1px solid var(--line)'
                }}
              >
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} size={16} style={{ color: '#ffd700', fill: '#ffd700' }} />
                  ))}
                </div>
                <p style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 16, color: 'var(--ink-2)' }}>
                  "{testimonial.content}"
                </p>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{testimonial.name}</div>
                  <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 36, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 16,
            color: 'var(--ink)'
          }}>
            Book Your Photographer
          </h2>
          <p style={{ 
            fontSize: 16, 
            color: 'var(--ink-2)', 
            textAlign: 'center', 
            marginBottom: 40
          }}>
            Fill in the details below and we'll get back to you within 24 hours
          </p>

          {submitted ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              background: 'var(--primary-tint)',
              borderRadius: 16,
              border: '1px solid var(--primary-soft)'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <h3 style={{ fontSize: 24, marginBottom: 8, color: 'var(--ink)' }}>
                Booking Request Received!
              </h3>
              <p style={{ color: 'var(--ink-2)' }}>
                We'll contact you within 24 hours to confirm details and answer any questions.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 16,
                    color: 'var(--ink)',
                    outline: 'none'
                  }}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 16,
                    color: 'var(--ink)',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 16,
                    color: 'var(--ink)',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  name="eventType"
                  placeholder="Event Type"
                  required
                  value={formData.eventType}
                  onChange={handleInputChange}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 16,
                    color: 'var(--ink)',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <input
                  type="date"
                  name="eventDate"
                  required
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 16,
                    color: 'var(--ink)',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  name="venue"
                  placeholder="Event Venue"
                  required
                  value={formData.venue}
                  onChange={handleInputChange}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 16,
                    color: 'var(--ink)',
                    outline: 'none'
                  }}
                />
              </div>

              <input
                type="text"
                name="attendees"
                placeholder="Expected Number of Attendees"
                value={formData.attendees}
                onChange={handleInputChange}
                style={{
                  padding: '14px 16px',
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                  background: 'var(--bg)',
                  fontSize: 16,
                  color: 'var(--ink)',
                  outline: 'none'
                }}
              />

              <select
                name="package"
                value={formData.package}
                onChange={handleInputChange}
                style={{
                  padding: '14px 16px',
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                  background: 'var(--bg)',
                  fontSize: 16,
                  color: 'var(--ink)',
                  outline: 'none'
                }}
              >
                <option value="basic">Basic Package - $275</option>
                <option value="standard">Standard Package - $899</option>
                <option value="premium">Premium Package - $1,499</option>
                <option value="custom">Custom Package - Custom Quote</option>
              </select>

              <textarea
                name="message"
                placeholder="Special requirements or questions..."
                rows={4}
                value={formData.message}
                onChange={handleInputChange}
                style={{
                  padding: '14px 16px',
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                  background: 'var(--bg)',
                  fontSize: 16,
                  color: 'var(--ink)',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />

              <button
                type="submit"
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                <Camera size={20} />
                Submit Booking Request
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Contact Info */}
      <section style={{ padding: '60px 24px', background: 'var(--bg-2)', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h3 style={{ fontSize: 24, marginBottom: 16, color: 'var(--ink)' }}>
            Questions? We're here to help!
          </h3>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-2)' }}>
              <Mail size={18} />
              <span>media@ventyvip.com</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
